import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Mail, Phone, Linkedin, MapPin, Briefcase, Building2, Shield, ChevronDown, ExternalLink, MessageSquare } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Sentry } from '../../lib/sentry';
import { useApi } from '../../lib/useApi';
import type { ContactWithAccount, ContactOutreach, ContactActionLog, ContactNote, ContactActionType, ChannelOutcome, CallBattlecard, ContactUseCaseScore } from '../../types';
import ContactMessagingTabs from './ContactMessagingTabs';
import ContactActivityTimeline from './ContactActivityTimeline';

interface Props {
  contact: ContactWithAccount;
  onClose: () => void;
  onUpdate: () => void;
}

type Tab = 'messaging' | 'activity';

interface ResolvedMember {
  user_id: string;
  role: string;
  display_name: string;
}

const SENIORITY_LABELS: Record<string, string> = {
  executive: 'Executive',
  executive_leadership: 'Executive Leadership',
  senior_leadership: 'Senior Leadership',
  department_leader: 'Department Leader',
  team_manager: 'Team Manager',
  individual_contributor: 'Individual Contributor',
};

export default function ContactDetailDrawer({ contact, onClose, onUpdate }: Props) {
  const { apiGet, apiPost, apiPut } = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('messaging');
  const [outreachList, setOutreachList] = useState<ContactOutreach[]>([]);
  const [actions, setActions] = useState<ContactActionLog[]>([]);
  const [notes, setNotes] = useState<ContactNote[]>([]);
  const [teamMembers, setTeamMembers] = useState<ResolvedMember[]>([]);
  const [assignedTo, setAssignedTo] = useState(contact.assigned_to || '');
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [generatingOutreach, setGeneratingOutreach] = useState(false);
  const [adjustingField, setAdjustingField] = useState<string | null>(null);
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);
  const autoAssignedRef = useRef<string | null>(null);

  useEffect(() => {
    autoAssignedRef.current = null;
    loadOutreach();
    loadTeamMembers();
  }, [contact.id]);

  useEffect(() => {
    if (activeTab === 'activity') loadActivity();
  }, [activeTab, contact.id]);

  async function loadOutreach() {
    setLoadingMessages(true);
    try {
      const data = await apiGet<{ outreach: ContactOutreach[] }>(
        `/v1/contacts/${contact.id}/outreach`,
      );
      setOutreachList(data.outreach || []);
    } catch {
      setOutreachList([]);
    }
    setLoadingMessages(false);
  }

  async function loadActivity() {
    setLoadingActivity(true);
    try {
      const data = await apiGet<{ actions: ContactActionLog[]; notes: ContactNote[] }>(
        `/v1/contacts/${contact.id}/activity`,
      );
      setActions(data.actions || []);
      setNotes(data.notes || []);
    } catch {
      setActions([]);
      setNotes([]);
    }
    setLoadingActivity(false);
  }

  async function loadTeamMembers() {
    let members: ResolvedMember[] = [];
    let ownerUserId: string | null = null;
    try {
      const data = await apiGet<{ members: ResolvedMember[]; owner_user_id: string | null }>(
        `/v1/account/${contact.account_id}/team-members`,
      );
      members = data.members || [];
      ownerUserId = data.owner_user_id ?? null;
    } catch {
      members = [];
      ownerUserId = null;
    }

    setTeamMembers(members);

    if (!contact.assigned_to && ownerUserId && autoAssignedRef.current !== contact.id) {
      autoAssignedRef.current = contact.id;
      setAssignedTo(ownerUserId);
      try {
        await apiPut(`/v1/contacts/${contact.id}`, { assigned_to: ownerUserId });
      } catch {
        // silent — best-effort auto-assign, keep prior no-op-on-error behaviour
      }
    }
  }

  async function handleAssign(userId: string) {
    setAssignedTo(userId);
    try {
      await apiPut(`/v1/contacts/${contact.id}`, { assigned_to: userId || null });
    } catch {
      // silent — keep prior no-op-on-error behaviour
    }
    onUpdate();
  }

  async function handleGenerateOutreach() {
    setGeneratingOutreach(true);
    try {
      // Resolve the contact's highest-relevance use case via the migrated
      // contact-scores read, then pick the top score client-side (the legacy
      // Supabase query ordered by use_case_relevance_score desc, limit 1).
      let topUseCaseTitle: string | null = null;
      try {
        const data = await apiGet<{ scores: ContactUseCaseScore[] }>(
          `/v1/account/${contact.account_id}/contact-scores`,
        );
        const top = (data.scores || [])
          .filter(s => s.contact_id === contact.id)
          .sort((a, b) => (b.use_case_relevance_score ?? 0) - (a.use_case_relevance_score ?? 0))[0];
        topUseCaseTitle = top?.use_case_title ?? null;
      } catch {
        topUseCaseTitle = null;
      }

      if (!topUseCaseTitle) {
        alert('This contact has no use case scores yet. Please run use case generation from the account first.');
        setGeneratingOutreach(false);
        return;
      }

      await apiPost('/v1/generate-contact-outreach', {
        account_id: contact.account_id,
        contact_id: contact.id,
        use_case_title: topUseCaseTitle,
      });

      await loadOutreach();
    } catch {
      // silent
    }
    setGeneratingOutreach(false);
  }

  async function handleFieldSave(outreachId: string, fieldKey: string, value: string) {
    // Backend whitelists the editable draft fields and resolves the owning account
    // from the outreach row (legacy UPDATE RLS is_account_org_member(account_id)).
    await apiPut(`/v1/outreach/${outreachId}`, { [fieldKey]: value });
    setOutreachList(prev => prev.map(o =>
      o.id === outreachId ? { ...o, [fieldKey]: value } : o
    ));
  }

  async function handleBattlecardSave(outreachId: string, updated: CallBattlecard) {
    await apiPut(`/v1/outreach/${outreachId}`, { call_battlecard: updated });
    setOutreachList(prev => prev.map(o =>
      o.id === outreachId ? { ...o, call_battlecard: updated } : o
    ));
  }

  async function handleAdjustWordRange(outreachId: string, fieldKey: string, minWords: number, maxWords: number) {
    const outreach = outreachList.find(o => o.id === outreachId);
    if (!outreach) return;
    const currentText = (outreach as unknown as Record<string, unknown>)[fieldKey] as string;
    if (!currentText) return;

    setAdjustingField(fieldKey);
    try {
      const result = await apiPost<{ adjusted_text?: string }>('/v1/adjust-outreach-length', {
        outreach_id: outreachId,
        field: fieldKey,
        current_text: currentText,
        min_words: minWords,
        max_words: maxWords,
      });
      if (result.adjusted_text) {
        setOutreachList(prev => prev.map(o =>
          o.id === outreachId ? { ...o, [fieldKey]: result.adjusted_text } : o
        ));
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'drawer_adjust_word_range' } });
    } finally {
      setAdjustingField(null);
    }
  }

  async function handleRegenerate(outreachId: string) {
    const outreach = outreachList.find(o => o.id === outreachId);
    if (!outreach) return;

    setRegeneratingId(outreachId);
    try {
      const result = await apiPost<{ outreach?: ContactOutreach }>('/v1/generate-contact-outreach', {
        account_id: contact.account_id,
        contact_id: contact.id,
        use_case_title: outreach.use_case_title,
      });
      if (result.outreach) {
        setOutreachList(prev => prev.map(o =>
          o.id === outreachId ? result.outreach! : o
        ));
      }
    } catch {
      // silent
    } finally {
      setRegeneratingId(null);
    }
  }

  async function handleLogAction(action: { action_type: ContactActionType; channel_outcome: ChannelOutcome; notes: string }) {
    try {
      // Backend derives account_id/org_id from the contact and user_id from the
      // authenticated caller (legacy INSERT RLS is_org_member(org_id) AND auth.uid()=user_id).
      await apiPost(`/v1/contacts/${contact.id}/action-logs`, action);
    } catch {
      // silent — keep prior no-op-on-error behaviour
    }
    try {
      await apiPut(`/v1/contacts/${contact.id}`, {
        last_contacted_at: new Date().toISOString(),
        last_activity_type: action.action_type,
      });
    } catch {
      // silent — best-effort activity stamp, keep prior no-op-on-error behaviour
    }
    loadActivity();
    onUpdate();
  }

  async function handleAddNote(text: string) {
    try {
      await apiPost(`/v1/contacts/${contact.id}/notes`, { note_text: text });
    } catch {
      // silent — keep prior no-op-on-error behaviour
    }
    loadActivity();
  }

  const seniorityLabel = SENIORITY_LABELS[contact.seniority] || contact.seniority;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-xl bg-app-surface shadow-2xl flex flex-col animate-slide-in-right overflow-hidden">
        <div className="flex-shrink-0 border-b border-app-line px-6 py-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-forest-100 flex items-center justify-center">
                <span className="text-lg font-bold text-forest-700">
                  {contact.first_name?.[0]}{contact.last_name?.[0]}
                </span>
              </div>
              <div>
                <h2 className="text-lg font-bold text-app-ink">{contact.first_name} {contact.last_name}</h2>
                <p className="text-sm text-app-muted">{contact.role || 'No role'}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-app-muted hover:text-app-muted hover:bg-app-canvas rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-app-canvas text-app-muted text-xs font-medium rounded-lg">
              <Building2 className="w-3 h-3" />
              {contact.account_company_name}
            </span>
            <button
              onClick={() => navigate(`/app/accounts/${contact.account_id}?tab=execute&contactId=${contact.id}`)}
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-forest-50 text-forest-700 text-xs font-medium rounded-lg hover:bg-forest-100 transition-colors"
              title="View outreach messaging in account"
            >
              <MessageSquare className="w-3 h-3" />
              View in Account
            </button>
            {seniorityLabel && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-app-focus/10 text-app-focus text-xs font-medium rounded-lg">
                <Briefcase className="w-3 h-3" />
                {seniorityLabel}
              </span>
            )}
            {contact.department && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-app-focus/10 text-app-focus text-xs font-medium rounded-lg">
                <Shield className="w-3 h-3" />
                {contact.department}
              </span>
            )}
          </div>

          <div className="mt-3 flex flex-wrap gap-3 text-xs text-app-muted">
            {contact.email && (
              <a href={`mailto:${contact.email}`} className="flex items-center gap-1 hover:text-forest-600 transition-colors">
                <Mail className="w-3 h-3" />
                {contact.email}
              </a>
            )}
            {contact.phone && (
              <a href={`tel:${contact.phone}`} className="flex items-center gap-1 hover:text-forest-600 transition-colors">
                <Phone className="w-3 h-3" />
                {contact.phone}
              </a>
            )}
            {contact.linkedin_url && (
              <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-app-focus transition-colors">
                <Linkedin className="w-3 h-3" />
                LinkedIn
                <ExternalLink className="w-2.5 h-2.5" />
              </a>
            )}
            {contact.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {contact.location}
              </span>
            )}
          </div>

          <div className="mt-4 flex items-center gap-3">
            <label className="text-[11px] font-semibold text-app-muted uppercase tracking-wider">Assigned to</label>
            <div className="relative flex-1 max-w-[200px]">
              <select
                value={assignedTo}
                onChange={e => handleAssign(e.target.value)}
                className="w-full px-3 py-1.5 bg-app-surface border border-app-line rounded-lg text-sm text-app-ink appearance-none focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-400"
              >
                <option value="">Unassigned</option>
                {teamMembers.map(m => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.user_id === user?.id ? `Me (${m.display_name})` : m.display_name}
                    {m.role === 'owner' ? ' (owner)' : ''}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-muted pointer-events-none" />
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 border-b border-app-line px-6">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab('messaging')}
              className={`py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'messaging'
                  ? 'border-forest-600 text-forest-700'
                  : 'border-transparent text-app-muted hover:text-app-ink'
              }`}
            >
              Messaging
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'activity'
                  ? 'border-forest-600 text-forest-700'
                  : 'border-transparent text-app-muted hover:text-app-ink'
              }`}
            >
              Activity & Notes
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {activeTab === 'messaging' && (
            loadingMessages ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-forest-300 border-t-forest-600 rounded-full animate-spin" />
              </div>
            ) : (
              <ContactMessagingTabs
                outreachList={outreachList}
                onGenerate={handleGenerateOutreach}
                generating={generatingOutreach}
                onFieldSave={handleFieldSave}
                onBattlecardSave={handleBattlecardSave}
                onAdjustWordRange={handleAdjustWordRange}
                adjustingField={adjustingField}
                onRegenerate={handleRegenerate}
                regeneratingId={regeneratingId}
              />
            )
          )}
          {activeTab === 'activity' && (
            <ContactActivityTimeline
              actions={actions}
              notes={notes}
              onLogAction={handleLogAction}
              onAddNote={handleAddNote}
              loading={loadingActivity}
            />
          )}
        </div>
      </div>
    </div>
  );
}
